
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { useGroups } from '@/hooks/useGroups';
import { useClients } from '@/hooks/useClients';
import { useUsers } from '@/hooks/useUsers';
import { GroupFormFields } from './GroupFormFields';
import { GroupFormActions } from './GroupFormActions';

const groupSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  client_id: z.string().optional(),
  responsible_user_id: z.string().optional(),
  status: z.enum(['active', 'inactive']),
  dias_semana: z.array(z.number().min(0).max(6)).optional(),
  inicio_turno: z.string().optional(),
  fim_turno: z.string().optional(),
});

type GroupFormData = z.infer<typeof groupSchema>;

interface NewGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewGroupDialog = ({ open, onOpenChange }: NewGroupDialogProps) => {
  const { addGroup } = useGroups();
  const { clients } = useClients();
  const { users } = useUsers();

  const form = useForm<GroupFormData>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      name: '',
      description: '',
      email: '',
      client_id: 'none',
      responsible_user_id: 'none',
      status: 'active',
      dias_semana: [],
      inicio_turno: '',
      fim_turno: '',
    },
  });

  const onSubmit = async (data: GroupFormData) => {
    const formData = {
      name: data.name,
      status: data.status,
      description: data.description || undefined,
      email: data.email || undefined,
      client_id: data.client_id === 'none' ? undefined : data.client_id,
      responsible_user_id: data.responsible_user_id === 'none' ? undefined : data.responsible_user_id,
      dias_semana: data.dias_semana && data.dias_semana.length > 0 ? data.dias_semana : undefined,
      inicio_turno: data.inicio_turno || undefined,
      fim_turno: data.fim_turno || undefined,
    };

    try {
      await addGroup.mutateAsync(formData);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao criar grupo:', error);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Grupo</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <GroupFormFields control={form.control} clients={clients} users={users} />
            <GroupFormActions 
              onCancel={handleCancel}
              isSubmitting={form.formState.isSubmitting}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
